import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import ModelCard from '../components/ModelCard';
import { keszHazakModels, getModelsByPriceRange, getModelsByType, formatPrice } from '../data/keszHazakModels';

const KeszHazak = () => {
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [areaRange, setAreaRange] = useState([0, 200]);
  const [buildingType, setBuildingType] = useState('all');
  const [availableOnly, setAvailableOnly] = useState(false);
  const [sortBy, setSortBy] = useState('price_asc');

  // Szűrt és rendezett modellek
  const filteredModels = useMemo(() => {
    let filtered = [...keszHazakModels];

    // Ár szűrés
    filtered = filtered.filter(model => 
      model.price_from_eur >= priceRange[0] && model.price_from_eur <= priceRange[1]
    );

    // Terület szűrés
    filtered = filtered.filter(model => 
      model.area_m2 >= areaRange[0] && model.area_m2 <= areaRange[1]
    );

    // Típus szűrés
    if (buildingType !== 'all') {
      filtered = getModelsByType(buildingType).filter(model => filtered.includes(model));
    }

    // Csak elérhető modellek
    if (availableOnly) {
      filtered = filtered.filter(model => model.status_badge === 'Aktuálisan elérhető');
    }

    // Rendezés
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price_asc':
          return a.price_from_eur - b.price_from_eur;
        case 'price_desc':
          return b.price_from_eur - a.price_from_eur;
        case 'area_asc':
          return a.area_m2 - b.area_m2;
        case 'area_desc':
          return b.area_m2 - a.area_m2;
        case 'name_asc':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return filtered;
  }, [priceRange, areaRange, buildingType, availableOnly, sortBy]);

  const resetFilters = () => {
    setPriceRange([0, 100000]);
    setAreaRange([0, 200]);
    setBuildingType('all');
    setAvailableOnly(false);
    setSortBy('price_asc');
  };

  return (
    <>
      <Helmet>
        <title>Kész Házak – Azonnal tervezhető modellek | HoppHomes</title>
        <meta name="description" content="Fedezd fel HoppHomes kész moduláris ház modelljeit: HH Lucara, HH Nativi, HH Martino, HH Verona, HH Kate és Garázs Thomas. Prémium minőség, gyors kivitelezés, okos otthon megoldások." />
        <meta property="og:title" content="Kész Házak – Azonnal tervezhető modellek | HoppHomes" />
        <meta property="og:description" content="HoppHomes moduláris házai azonnal tervezhetők, látványtervekkel és személyre szabható opciókkal. Prémium minőség, magyar szakmai háttér." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://hopphomes.hu/kesz-hazak" />
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Page Header */}
        <div className="border-b border-white/[0.06] px-6 py-5">
          <h1 className="text-2xl font-black text-white">Kész Házak</h1>
          <p className="text-[#9ca3af] text-sm mt-0.5">Azonnal tervezhető modellek · az árak a műszaki tartalomtól és helyszíntől függnek</p>
        </div>

        <div className="max-w-7xl mx-auto px-4 pb-20">
          {/* Szűrők */}
          <div className="bg-[#161616] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-6 mb-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h2 className="text-xl font-bold text-white">Szűrők</h2>
              <button
                onClick={resetFilters}
                className="text-sm text-gray-300 hover:text-white transition-colors"
              >
                Szűrők törlése
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Ár szűrő */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Ár ({formatPrice(priceRange[0])} - {formatPrice(priceRange[1])})
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="100000"
                    step="5000"
                    value={priceRange[0]}
                    onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
                    className="w-full"
                  />
                  <input
                    type="range"
                    min="0"
                    max="100000"
                    step="5000"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Terület szűrő */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Alapterület ({areaRange[0]} - {areaRange[1]} m²)
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min="0"
                    max="200"
                    step="10"
                    value={areaRange[0]}
                    onChange={(e) => setAreaRange([parseInt(e.target.value), areaRange[1]])}
                    className="w-full"
                  />
                  <input
                    type="range"
                    min="0"
                    max="200"
                    step="10"
                    value={areaRange[1]}
                    onChange={(e) => setAreaRange([areaRange[0], parseInt(e.target.value)])}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Típus szűrő */}
              <div>
                <label className="block text-white font-medium mb-2">Épület típus</label>
                <select
                  value={buildingType}
                  onChange={(e) => setBuildingType(e.target.value)}
                  className="w-full bg-white/10 border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                >
                  <option value="all">Összes</option>
                  <option value="bungalo">Bungaló</option>
                  <option value="garazs">Garázs</option>
                </select>
              </div>

              {/* Rendezés */}
              <div>
                <label className="block text-white font-medium mb-2">Rendezés</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full bg-white/10 border border-white/[0.08] rounded-lg px-3 py-2 text-white"
                >
                  <option value="price_asc">Ár (növekvő)</option>
                  <option value="price_desc">Ár (csökkenő)</option>
                  <option value="area_asc">Terület (növekvő)</option>
                  <option value="area_desc">Terület (csökkenő)</option>
                  <option value="name_asc">Név (A-Z)</option>
                </select>
              </div>
            </div>

            {/* Elérhető csak checkbox */}
            <div className="mt-4">
              <label className="flex items-center text-white">
                <input
                  type="checkbox"
                  checked={availableOnly}
                  onChange={(e) => setAvailableOnly(e.target.checked)}
                  className="mr-2"
                />
                Csak azonnal elérhető modellek
              </label>
            </div>
          </div>

          {/* Eredmények */}
          <div className="mb-6">
            <p className="text-gray-300">
              <span className="font-semibold">{filteredModels.length}</span> modell találat
            </p>
          </div>

          {/* Modellek grid */}
          {filteredModels.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredModels.map((model) => (
                <ModelCard key={model.slug} model={model} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🏠</div>
              <h3 className="text-xl font-bold text-white mb-2">Nincs találat</h3>
              <p className="text-gray-400 mb-6">
                A megadott szűrési feltételeknek nem felel meg egyetlen modell sem.
              </p>
              <button
                onClick={resetFilters}
                className="bg-[#e31937] hover:bg-[#b40000] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Szűrők törlése
              </button>
            </div>
          )}

          {/* CTA szekció */}
          <div className="mt-16 text-center">
            <div className="bg-[#161616] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-8">
              <h2 className="text-2xl font-bold text-white mb-4">
                Nem találod a megfelelő modellt?
              </h2>
              <p className="text-gray-300 mb-6">
                Egyedi igényeid alapján is tervezünk! Vedd fel velünk a kapcsolatot személyre szabott ajánlatért.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-[#e31937] hover:bg-[#b40000] text-white font-semibold py-3 px-6 rounded-lg transition-colors">
                  Kérek egyedi ajánlatot
                </button>
                <button className="border border-white/30 hover:bg-white/10 text-white font-semibold py-3 px-6 rounded-lg transition-colors">
                  Kapcsolatfelvétel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default KeszHazak;
